import { message } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const useSurveys = ({ countryCode, setSurveyDataToSave, finishSurvey }) => {
  const navigate = useNavigate();
  const [surveyData, setSurveyData] = useState();
  const [surveys, setSurveys] = useState();
  const [currentSurvey, setCurrentSurvey] = useState();
  const [questionFromSummary, setQuestionFromSummary] = useState();
  const [currentIndex, setCurrentIndex] = useState(0);

  const goBackExams = useCallback(() => {
    localStorage.setItem('surveyData', JSON.stringify({
      ...JSON.parse(localStorage.getItem('surveyData')),
      back_to_exams: true
    }));
    navigate(`/mis-examenes/${countryCode}`);
  }, [countryCode, navigate]);

  useEffect(() => {
    if (surveyData) {
      let newSurveys = []
      if (questionFromSummary) {
        newSurveys = surveyData.map((survey) => {
          if (survey.questions.find(question => question.question_id === questionFromSummary.question_id)) {
            return {
              ...survey,
              current: true
            }
          } else {
            return {
              ...survey,
              current: false
            }
          }
        });
      } else {
        const searchedCurrentSurvey = surveyData.find(survey => survey.current);

        newSurveys = surveyData.map((survey, index) => {
          if (searchedCurrentSurvey) {
            return {
              ...survey,
              current: survey.id === searchedCurrentSurvey.id
            }
          } else {
            return {
              ...survey,
              current: index === 0
            }
          }
        });
      }
      setSurveys(newSurveys);
    }
  }, [surveyData, questionFromSummary]);

  useEffect(() => {
    if (surveys) {
      setSurveyDataToSave(surveys);
      setCurrentSurvey(surveys.find(survey => survey.current));
      setCurrentIndex(surveys.findIndex(survey => survey.current));
    }
  }, [setSurveyDataToSave, surveys]);

  const checkIfAllQuestionsAnswered = useCallback(() => {
    if (currentSurvey) {
      const unansweredQuestions = currentSurvey.questions.filter(question => {
        if (question.required_field === false) {
          return false;
        }

        if (question.type === 'select' || question.type === 'select_multiple' || question.type === 'radio') {
          if (!question.selected_option) return true;
          if (Array.isArray(question.selected_option) && question.selected_option.length === 0) return true;
          return false;
        }
        return !question.selected_option || !question.selected_option.value;
      });

      return unansweredQuestions.length === 0;
    }
  }, [currentSurvey]);

  const nextSurvey = useCallback(() => {
    if (checkIfAllQuestionsAnswered()) {
      const newSurveys = surveys.map((survey, index) => {
        if (index === currentIndex) {
          return {
            ...survey,
            current: false
          }
        } else if (index === currentIndex + 1) {
          return {
            ...survey,
            current: true
          }
        } else {
          return survey;
        }
      });

      setSurveys(newSurveys);
    } else {
      message.error({
        content: 'Por favor, responda todas as perguntas antes de continuar.',
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });
    }
  }, [checkIfAllQuestionsAnswered, currentIndex, surveys]);

  const continueButton = useCallback(() => {
    if (!questionFromSummary && currentIndex + 1 < surveys.length) {
      nextSurvey();
    } else {
      finishSurvey();
      navigate(`/mis-examenes/${countryCode}/encuesta-salud/sumario`);
      window.scrollTo(0, 0, { behavior: 'smooth' });
    }
  }, [countryCode, currentIndex, finishSurvey, navigate, nextSurvey, surveys, questionFromSummary]);

  const respondSelectionQuestions = useCallback((question, option) => {
    return surveys.map((survey) => {
      if (survey.survey_id === currentSurvey.survey_id) {
        return {
          ...survey,
          questions: survey.questions.map((surveyQuestion) => {
            if (surveyQuestion.question_id !== question.question_id) return surveyQuestion;
            if (surveyQuestion.type === 'select_multiple') {
              const currentSelected = Array.isArray(surveyQuestion.selected_option) ? surveyQuestion.selected_option : [];
              const isAlreadySelected = currentSelected.some(item => item.value === option.value);
              return {
                ...surveyQuestion,
                selected_option: isAlreadySelected
                  ? currentSelected.filter(item => item.value !== option.value)
                  : [...currentSelected, option]
              };
            }
            return { ...surveyQuestion, selected_option: option };
          })
        };
      } else {
        return survey;
      }
    });
  }, [currentSurvey, surveys]);

  const respondTextQuestions = useCallback((question, text) => {
    return surveys.map((survey) => {
      if (survey.survey_id === currentSurvey.survey_id) {
        return {
          ...survey,
          questions: survey.questions.map((q) => {
            if (q.question_id === question.question_id) {
              return {
                ...q,
                selected_option: {
                  value: text
                }
              }
            } else {
              return q;
            }
          })
        }
      } else {
        return survey;
      }
    });
  }, [currentSurvey, surveys]);

  const selectOption = useCallback((question, option) => {
    if (currentSurvey) {
      let newSurveys;
      if (question.type === 'select' || question.type === 'select_multiple' || question.type === 'radio') {
        newSurveys = respondSelectionQuestions(question, option)
      } else {
        newSurveys = respondTextQuestions(question, option)
      }

      setSurveys(newSurveys);
    }
  }, [currentSurvey, respondSelectionQuestions, respondTextQuestions]);

  return {
    setSurveyData,
    setQuestionFromSummary,
    surveys,
    currentSurvey,
    currentIndex,
    selectOption,
    continueButton,
    checkIfAllQuestionsAnswered,
    questionFromSummary,
    goBackExams,
    examName: JSON.parse(localStorage.getItem('surveyData'))?.exam_name,
  };
}

export default useSurveys;